import * as StellarSdk from '@stellar/stellar-sdk';

import { ICE_TO_DELEGATE } from 'constants/assets';
import { DELEGATE_MARKER_KEY } from 'constants/stellar-accounts';

import { getEnvClassicAssetData } from 'helpers/assets';
import { getVoteClaimBackDate } from 'helpers/dao';

import EventService from 'services/event.service';
import { StellarEvents, StellarPayload } from 'services/stellar/events/events';
import Horizon from 'services/stellar/horizon/horizon';

import { ProposalSimple, ProposalVoteClaim, VoteChoiceSimple } from 'types/governance';
import { ClaimableBalance } from 'types/stellar';
import { PairStats } from 'types/vote';

export default class ClaimableBalances {
    private readonly horizon: Horizon;
    private readonly event: EventService<StellarEvents, StellarPayload>;
    private claimableBalances: StellarSdk.Horizon.ServerApi.ClaimableBalanceRecord[] | null = null;
    // The account whose claimable balances are cached in full; null while later pages load.
    private completeFor: string | null = null;

    constructor(horizon: Horizon, event: EventService<StellarEvents, StellarPayload>) {
        this.horizon = horizon;
        this.event = event;
    }

    get isClaimableBalancesLoaded() {
        return this.claimableBalances !== null;
    }

    getClaimableBalances(publicKey: string) {
        const limit = 200;
        this.horizon.server
            .claimableBalances()
            .claimant(publicKey)
            .order('desc')
            .limit(limit)
            .call()
            .then(claimable => {
                this.claimableBalances = claimable.records;
                this.completeFor = claimable.records.length < limit ? publicKey : null;
                this.event.trigger({ type: StellarEvents.claimableUpdate });

                if (claimable.records.length === limit) {
                    this.getNextClaimableBalances(claimable.next, limit, publicKey);
                }
            });
    }

    async loadClaimableBalances(
        publicKey: string,
        publish = true,
    ): Promise<StellarSdk.Horizon.ServerApi.ClaimableBalanceRecord[]> {
        const limit = 200;
        let page = await this.horizon.server
            .claimableBalances()
            .claimant(publicKey)
            .order('desc')
            .limit(limit)
            .call();
        let records = page.records;

        while (page.records.length === limit) {
            page = await page.next();
            records = [...records, ...page.records];
        }

        if (publish) {
            this.claimableBalances = records;
            this.completeFor = publicKey;
            this.event.trigger({ type: StellarEvents.claimableUpdate });
        }

        return records;
    }

    getNextClaimableBalances(
        next: () => Promise<
            StellarSdk.Horizon.ServerApi.CollectionPage<StellarSdk.Horizon.ServerApi.ClaimableBalanceRecord>
        >,
        limit: number,
        publicKey: string,
    ) {
        next().then(res => {
            this.claimableBalances = [...this.claimableBalances, ...res.records];
            this.completeFor = res.records.length < limit ? publicKey : null;
            this.event.trigger({ type: StellarEvents.claimableUpdate });

            if (res.records.length === limit) {
                this.getNextClaimableBalances(res.next, limit, publicKey);
            }
        });
    }

    // The cached list when it holds every claimable balance of the account, otherwise null.
    getCachedClaimableBalances(publicKey: string) {
        return this.completeFor === publicKey ? this.claimableBalances : null;
    }

    getLocks(publicKey: string, claimableBalances = this.claimableBalances) {
        if (!claimableBalances) {
            return null;
        }

        const { assetString: aquaAssetString } = getEnvClassicAssetData('aqua');

        return claimableBalances.filter(
            claim =>
                claim.claimants.length === 1 &&
                claim.claimants[0].destination === publicKey &&
                claim.asset === aquaAssetString,
        );
    }

    getMarketVotesValue(marketKey: string, accountId: string, asset: StellarSdk.Asset) {
        if (!this.claimableBalances) {
            return null;
        }

        return this.claimableBalances.reduce((acc, claim) => {
            if (claim.claimants.length !== 2) {
                return acc;
            }
            const hasMarker = claim.claimants.some(claimant => claimant.destination === marketKey);
            const hasSelfClaim = claim.claimants.some(
                claimant => claimant.destination === accountId,
            );
            const isAqua = claim.asset === `${asset.code}:${asset.issuer}`;

            if (hasMarker && hasSelfClaim && isAqua) {
                acc += Number(claim.amount);
            }
            return acc;
        }, 0);
    }

    getVotesForProposal(
        proposal: ProposalSimple,
        accountId: string,
        claimableBalances = this.claimableBalances,
    ): (StellarSdk.Horizon.ServerApi.ClaimableBalanceRecord & ProposalVoteClaim)[] | null {
        if (!claimableBalances) {
            return null;
        }

        const { assetString: aquaAssetString } = getEnvClassicAssetData('aqua');
        const { assetString: governIceAssetString } = getEnvClassicAssetData('governIce');
        const { assetString: gdIceAssetString } = getEnvClassicAssetData('gdIce');

        const getVoteChoice = (
            claim: StellarSdk.Horizon.ServerApi.ClaimableBalanceRecord,
        ): VoteChoiceSimple | null => {
            const hasMarker = (marker?: string) =>
                Boolean(marker) &&
                claim.claimants.some(({ destination }) => destination === marker);

            if (hasMarker(proposal.vote_for_issuer)) {
                return 'vote_for';
            }
            if (hasMarker(proposal.vote_against_issuer)) {
                return 'vote_against';
            }
            if (hasMarker(proposal.abstain_issuer)) {
                return 'vote_abstain';
            }
            return null;
        };

        return claimableBalances.reduce((acc, claim) => {
            if (claim.claimants.length !== 2) {
                return acc;
            }
            const voteChoice = getVoteChoice(claim);
            const selfClaim = claim.claimants.find(claimant => claimant.destination === accountId);
            // Only balances the account can claim back once the voting ends are its votes.
            const claimBackDate = selfClaim ? getVoteClaimBackDate(selfClaim.predicate) : null;
            const isAqua = claim.asset === aquaAssetString;
            const isGovIce = claim.asset === governIceAssetString;
            const isGDIce = claim.asset === gdIceAssetString;

            if (voteChoice && claimBackDate && (isAqua || isGovIce || isGDIce)) {
                const [code, issuer] = claim.asset.split(':');
                acc.push({
                    ...claim,
                    voteChoice,
                    claimBackDate,
                    assetCode: code,
                    assetIssuer: issuer,
                });
            }
            return acc;
        }, []);
    }

    getPairVotes(pair: PairStats, accountId: string) {
        if (!this.claimableBalances) {
            return null;
        }
        const { assetString: aquaAssetString } = getEnvClassicAssetData('aqua');
        const { assetString: upvoteIceAssetString } = getEnvClassicAssetData('upvoteIce');
        const { assetString: dIceAssetString } = getEnvClassicAssetData('dIce');

        return this.claimableBalances.reduce((acc, claim) => {
            if (claim.claimants.length !== 2) {
                return acc;
            }
            const hasUpMarker = claim.claimants.some(
                claimant => claimant.destination === pair.account_id,
            );
            const selfClaim = claim.claimants.find(claimant => claimant.destination === accountId);
            const isAqua = claim.asset === aquaAssetString;
            const isUpIce = claim.asset === upvoteIceAssetString;
            const isDelegatedIce = claim.asset === dIceAssetString;

            if (hasUpMarker && Boolean(selfClaim) && (isAqua || isUpIce || isDelegatedIce)) {
                const [code, issuer] = claim.asset.split(':');
                acc.push({
                    ...claim,
                    claimBackDate: selfClaim.predicate.not.abs_before,
                    assetCode: code,
                    assetIssuer: issuer,
                });
            }
            return acc;
        }, []);
    }

    getKeysSimilarToMarketKeys(accountId: string): string[] {
        if (!this.claimableBalances) {
            return null;
        }

        const { assetString: aquaAssetString } = getEnvClassicAssetData('aqua');
        const { assetString: upvoteIceAssetString } = getEnvClassicAssetData('upvoteIce');
        const { assetString: dIceAssetString } = getEnvClassicAssetData('dIce');

        return this.claimableBalances.reduce((acc, claim) => {
            if (claim.claimants.length !== 2) {
                return acc;
            }
            const isAqua = claim.asset === aquaAssetString;
            const isUpIce = claim.asset === upvoteIceAssetString;
            const isDelegatedIce = claim.asset === dIceAssetString;
            const hasSelfClaim = claim.claimants.some(
                claimant => claimant.destination === accountId,
            );
            if ((isAqua || isUpIce || isDelegatedIce) && hasSelfClaim) {
                const similarToMarketKey = claim.claimants.find(
                    claimant => claimant.destination !== accountId,
                );

                if (!similarToMarketKey) {
                    return acc;
                }
                if (!acc.includes(similarToMarketKey.destination)) {
                    acc.push(similarToMarketKey.destination);
                }
            }
            return acc;
        }, []);
    }

    getDelegateLocks(accountId: string) {
        if (!this.claimableBalances) {
            return null;
        }

        return this.claimableBalances
            .reduce((acc, claim) => {
                if (claim.claimants.length !== 3) {
                    return acc;
                }
                const hasMarker = claim.claimants.some(
                    claimant => claimant.destination === DELEGATE_MARKER_KEY,
                );
                const selfClaim = claim.claimants.find(
                    claimant =>
                        claimant.destination === accountId && !!claimant.predicate?.not?.abs_before,
                );

                const isDelegatedIce = ICE_TO_DELEGATE.includes(claim.asset);

                if (hasMarker && Boolean(selfClaim) && isDelegatedIce) {
                    acc.push(claim);
                }
                return acc;
            }, [])
            .map(cb => {
                const unlockDate =
                    cb.claimants.find(({ destination }) => destination === accountId).predicate.not
                        .abs_before_epoch * 1000;

                return { ...cb, unlockDate };
            });
    }

    getDelegatorLocks(accountId: string): ClaimableBalance[] {
        if (!this.claimableBalances) {
            return null;
        }

        return this.claimableBalances.reduce((acc, claim) => {
            if (claim.claimants.length !== 3) {
                return acc;
            }
            const hasMarker = claim.claimants.some(
                claimant => claimant.destination === DELEGATE_MARKER_KEY,
            );
            const selfClaim = claim.claimants.find(
                claimant =>
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    claimant.destination === accountId && !!claimant.predicate?.not?.unconditional,
            );

            const isDelegatedIce = ICE_TO_DELEGATE.includes(claim.asset);

            if (hasMarker && Boolean(selfClaim) && isDelegatedIce) {
                acc.push(claim);
            }
            return acc;
        }, []);
    }

    clearClaimableBalances() {
        this.claimableBalances = null;
        this.completeFor = null;
    }
}
